import type { MaybeRefOrGetter } from 'vue';
import { BCP47_LANGUAGE_TAG_REGEX, normalizeLanguageTag } from '@nova/core';
import { isString } from 'class-validator';

import { useLocalStorageRef } from './useLocalStorage';

export const LOCALE_STORAGE_KEY = 'language';

/**
 * Maps locale codes with region/country variants to their base language code.
 * Used to normalize specific locales (e.g., 'es-mx') to generic language codes (e.g., 'es').
 */
export const localeToBaseLanguageMap = Object.freeze({
  'es-mx': 'es',
  'fr-ca': 'fr',
  'pt-br': 'pt'
});

/**
 * Maps locale codes to their corresponding Power BI locale slugs.
 * Unrecognised locales fall back to `en-US` via `getPowerBiLocale`.
 */
export const localeToPowerBiLocaleMap = Object.freeze({
  en: 'en-US',
  'es-mx': 'es-MX',
  'pt-br': 'pt-BR',
  'fr-ca': 'fr-CA'
});

const DEFAULT_POWER_BI_LOCALE = 'en-US';

/**
 * Returns the Power BI locale slug for a given Helios locale code.
 * Falls back to `en-US` when the locale is not one of the four supported slugs.
 *
 * @param locale - A Helios locale code (e.g. `'pt-br'`, `'en'`)
 * @returns A Power BI-compatible locale slug (e.g. `'pt-BR'`, `'en-US'`)
 *
 * @example
 * getPowerBiLocale('pt-br')  // 'pt-BR'
 * getPowerBiLocale('unknown') // 'en-US'
 */
export function getPowerBiLocale(locale: string): string {
  const map: Record<string, string> = localeToPowerBiLocaleMap;
  return map[locale.toLowerCase()] ?? DEFAULT_POWER_BI_LOCALE;
}

/**
 * Maps supported locales to human-readable language labels and flag emojis.
 * Used for displaying locale options in UI components like locale selectors.
 */
export const localeToReadableLanguageMap = Object.freeze({
  en: { label: 'English (US)', flag: '🇺🇸' },
  'es-mx': { label: 'Español (MX)', flag: '🇲🇽' },
  'fr-ca': { label: 'Français (CA)', flag: '🇨🇦' },
  'pt-br': { label: 'Português (BR)', flag: '🇧🇷' },
  zh: { label: '简体中文 (CN)', flag: '🇨🇳' },
  'zh-tw': { label: '繁體中文 (TW)', flag: '🇹🇼' },
  ja: { label: '日本語 (JP)', flag: '🇯🇵' },
  ko: { label: '한국어 (KR)', flag: '🇰🇷' },
  id: { label: 'Bahasa Indonesia (ID)', flag: '🇮🇩' },
  th: { label: 'ไทย (TH)', flag: '🇹🇭' },
  vi: { label: 'Tiếng Việt (VN)', flag: '🇻🇳' }
});

function isBaseLanguageLocaleKey(key: string): key is keyof typeof localeToBaseLanguageMap {
  return Object.hasOwn(localeToBaseLanguageMap, key);
}

function isReadableLanguageLocaleKey(key: string): key is keyof typeof localeToReadableLanguageMap {
  return Object.hasOwn(localeToReadableLanguageMap, key);
}

/**
 * Returns true when the locale is one of the app UI locales in
 * {@link localeToReadableLanguageMap}.
 */
export function isAppSupportedI18nLocale(locale: string): boolean {
  return Object.hasOwn(localeToReadableLanguageMap, locale.toLowerCase());
}

/**
 * Converts a vue-i18n locale code to a BCP 47 tag for API/DB persistence.
 * Uses the same normalization rules as Neutron ({@link normalizeLanguageTag}).
 */
export function i18nLocaleToPreferredLanguage(locale: string): string | undefined {
  const normalized = normalizeLanguageTag(locale);
  if (!isString(normalized) || normalized === '' || !BCP47_LANGUAGE_TAG_REGEX.test(normalized)) {
    return undefined;
  }

  return normalized;
}

/**
 * Converts a BCP 47 `preferredLanguage` tag from the API/DB to a vue-i18n locale code
 * only when the tag maps to one of the app UI locales in
 * {@link localeToReadableLanguageMap}.
 */
export function preferredLanguageToI18nLocale(tag: string | null | undefined): string | undefined {
  if (!tag) {
    return undefined;
  }

  const normalized = normalizeLanguageTag(tag);
  if (!isString(normalized) || !BCP47_LANGUAGE_TAG_REGEX.test(normalized)) {
    return undefined;
  }

  const i18nLocale = normalized.toLowerCase();
  if (isAppSupportedI18nLocale(i18nLocale)) {
    return i18nLocale;
  }

  const language = i18nLocale.split('-')[0].toLowerCase();
  if (!language) {
    return undefined;
  }

  for (const appLocale of Object.keys(localeToReadableLanguageMap)) {
    const regionalAppLocale = appLocale as keyof typeof localeToBaseLanguageMap;
    if (appLocale === language || localeToBaseLanguageMap[regionalAppLocale] === language) {
      return appLocale;
    }
  }

  return undefined;
}

/**
 * Returns true when the API/DB tag is present but not one of the app UI locales.
 * Empty/null tags are not "unsupported" — they mean no preference is stored yet.
 */
export function isUnsupportedAppPreferredLanguage(tag: string | null | undefined): boolean {
  if (typeof tag !== 'string' || tag.trim() === '') {
    return false;
  }

  return preferredLanguageToI18nLocale(tag) === undefined;
}

/** Default locale to use when no preference is set or detected. */
const DEFAULT_LOCALE = 'en';

/**
 * Composable for managing user language/locale preferences with browser detection fallback.
 * Stores preferences per user in localStorage and provides utilities for locale normalization and display.
 *
 * @param userId - Optional user ID to scope localStorage to a specific user. Allows different users on the same device to have different locale preferences.
 * @returns Object containing locale state and utility functions for managing and retrieving locale information.
 */
export function useLocaleHelpers(userId?: MaybeRefOrGetter<string | undefined>) {
  const {
    ref: localeRef,
    set: setLocale,
    get: getLocale
  } = useLocalStorageRef(
    LOCALE_STORAGE_KEY,
    DEFAULT_LOCALE,
    userId !== undefined ? { userId } : undefined
  );

  /**
   * Converts a locale code to its base language code.
   * Handles region-specific locales by mapping them to their base language.
   *
   * @param locale - The locale code to normalize (e.g., 'es-mx', 'en-US')
   * @returns The base language code (e.g., 'es', 'en'), or the original locale if no mapping exists
   *
   * @example
   * getBaseLanguageCodeFromLocale('es-mx') // Returns 'es'
   * getBaseLanguageCodeFromLocale('en-US') // Returns 'en'
   */
  function getBaseLanguageCodeFromLocale(locale: string) {
    const key = locale.toLowerCase();
    return isBaseLanguageLocaleKey(key) ? localeToBaseLanguageMap[key] : key;
  }

  /**
   * Retrieves the human-readable label for a locale.
   * Used for displaying language options to users in UI components.
   *
   * @param locale - The locale code to look up (e.g., 'es-mx', 'en')
   * @returns The readable language label (e.g., 'Español (MX)'), or the original locale if not found
   *
   * @example
   * getReadableLanguageFromLocale('es-mx') // Returns 'Español (MX)'
   */
  function getReadableLanguageFromLocale(locale: string) {
    const key = locale.toLowerCase();
    return isReadableLanguageLocaleKey(key) ? localeToReadableLanguageMap[key].label : key;
  }

  return {
    localeRef,
    getBaseLanguageCodeFromLocale,
    setLocale,
    getReadableLanguageFromLocale,
    getLocale
  };
}
